import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, Send, CheckCircle, X } from "lucide-react";
import { estimérRevenus, createLead } from "@/lib/api";
import { toast } from "sonner";

export function EstimateurSection() {
  const [estimateur, setEstimateur] = useState({ type_bien: "appartement", surface: 50, capacite: 4 });
  const [estimation, setEstimation] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contact, setContact] = useState({ nom: "", email: "", telephone: "", ville: "" });

  const handleEstimation = async () => {
    try {
      const res = await estimérRevenus(estimateur);
      setEstimation(res.data);
    } catch (error) {
      console.error("Error estimating:", error);
      toast.error("Erreur lors du calcul");
    }
  };

  const handleSendStudy = async (e) => {
    e.preventDefault();
    if (!contact.nom || !contact.email) return;
    setSubmitting(true);
    try {
      await createLead({
        type_lead: "proprietaire",
        nom: contact.nom,
        email: contact.email,
        telephone: contact.telephone || undefined,
        ville: contact.ville || undefined,
        type_bien: estimateur.type_bien,
        surface: estimateur.surface,
        capacite: estimateur.capacite,
        objectif: "etude_gratuite",
        message: `Demande d'étude gratuite via l'estimateur. Estimation : ${estimation?.revenu_mensuel_estime} EUR/mois (${estimation?.taux_occupation_estime} d'occupation).`,
      });
      setSent(true);
      setShowForm(false);
      toast.success("Votre demande a bien été envoyée !");
    } catch {
      toast.error("Erreur lors de l'envoi, veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-[#F5F0EB]" data-testid="estimateur-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* Left — estimator inputs */}
          <div>
            <p className="text-[#C9922A] uppercase tracking-[0.2em] text-sm font-semibold mb-3">Pour les propriétaires</p>
            <h2 className="text-3xl md:text-4xl text-[#004333] mb-6">Estimez vos revenus en 1 minute</h2>
            <p className="text-[#6B5E50] mb-8">
              Découvrez le potentiel de votre bien en location courte durée à Limoges. Notre algorithme calcule une estimation basée sur le marché local.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Type de bien</label>
                <select
                  value={estimateur.type_bien}
                  onChange={(e) => setEstimateur({ ...estimateur, type_bien: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200/80 focus:border-[#C9922A]/60 focus:ring-2 focus:ring-[#C9922A]/8 outline-none transition-all bg-white rounded-sm appearance-none"
                  data-testid="estimateur-type"
                >
                  <option value="studio">Studio</option>
                  <option value="appartement">Appartement</option>
                  <option value="maison">Maison</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Surface (m²)</label>
                  <input
                    type="number"
                    value={estimateur.surface}
                    onChange={(e) => setEstimateur({ ...estimateur, surface: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200/80 focus:border-[#C9922A]/60 outline-none transition-all rounded-sm"
                    data-testid="estimateur-surface"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Capacité (pers.)</label>
                  <input
                    type="number"
                    value={estimateur.capacite}
                    onChange={(e) => setEstimateur({ ...estimateur, capacite: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-200/80 focus:border-[#C9922A]/60 outline-none transition-all rounded-sm"
                    data-testid="estimateur-capacite"
                  />
                </div>
              </div>

              <button onClick={handleEstimation} className="btn-primary w-full" data-testid="estimateur-submit">
                Calculer mes revenus
              </button>
            </div>
          </div>

          {/* Right — results + CTA */}
          <div>
            {estimation ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="bg-[#FFFDF9] p-8 rounded-xl border border-[#E0D6CA] shadow-[0_4px_24px_rgba(44,30,20,0.08)]">
                <h3 className="text-2xl text-[#004333] mb-6">Votre estimation</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-[#E8E0D4]">
                    <span className="text-[#6B5E50]">Prix par nuit estimé</span>
                    <span className="font-mono text-xl font-bold text-[#004333]">{estimation.prix_nuit_estime} €</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#E8E0D4]">
                    <span className="text-[#6B5E50]">Revenu mensuel estimé</span>
                    <span className="font-mono text-xl font-bold text-[#C9922A]">{estimation.revenu_mensuel_estime} €</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-[#E8E0D4]">
                    <span className="text-[#6B5E50]">Revenu annuel estimé</span>
                    <span className="font-mono text-2xl font-bold text-[#004333]">{estimation.revenu_annuel_estime} €</span>
                  </div>
                  <p className="text-xs text-[#8B7D6B] pt-2">
                    *Estimation basée sur un taux d'occupation de {estimation.taux_occupation_estime}
                  </p>
                </div>

                {/* CTA — étude gratuite */}
                {sent ? (
                  <div className="mt-6 flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-700 font-medium">
                      Demande envoyée ! Nous vous recontactons sous 24h.
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowForm(true)}
                    className="btn-accent w-full mt-6 flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Demander une étude gratuite
                  </button>
                )}
              </motion.div>
            ) : (
              <div className="bg-[#FFFDF9] p-8 text-center rounded-xl border border-[#E0D6CA] shadow-[0_2px_8px_rgba(44,30,20,0.04)]">
                <div className="w-16 h-16 bg-[#C9922A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-[#C9922A]" />
                </div>
                <h3 className="text-xl text-[#004333] mb-2">Découvrez votre potentiel</h3>
                <p className="text-[#8B7D6B] text-sm">Remplissez le formulaire pour obtenir votre estimation personnalisée.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== MODAL ÉTUDE GRATUITE ===== */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#FFFDF9] w-full max-w-md rounded-2xl shadow-2xl border border-[#E0D6CA] overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-[#E0D6CA] bg-[#004333]">
                <div>
                  <h3 className="text-white font-semibold text-lg">Demande d'étude gratuite</h3>
                  <p className="text-white/60 text-sm mt-0.5">Réponse sous 24h</p>
                </div>
                <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Recap estimation */}
              {estimation && (
                <div className="mx-6 mt-5 p-3 bg-[#C9922A]/8 border border-[#C9922A]/20 rounded-xl flex items-center justify-between gap-4">
                  <span className="text-sm text-[#6B5E50]">Votre estimation</span>
                  <span className="font-mono font-bold text-[#C9922A] text-lg">{estimation.revenu_mensuel_estime} € / mois</span>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSendStudy} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#2D2418] mb-1.5">Nom complet <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={contact.nom}
                    onChange={(e) => setContact({ ...contact, nom: e.target.value })}
                    placeholder="Jean Dupont"
                    className="input-hh"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D2418] mb-1.5">Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    required
                    value={contact.email}
                    onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    placeholder="jean@email.fr"
                    className="input-hh"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#2D2418] mb-1.5">Téléphone</label>
                    <input
                      type="tel"
                      value={contact.telephone}
                      onChange={(e) => setContact({ ...contact, telephone: e.target.value })}
                      placeholder="06 XX XX XX XX"
                      className="input-hh"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2D2418] mb-1.5">Ville du bien</label>
                    <input
                      type="text"
                      value={contact.ville}
                      onChange={(e) => setContact({ ...contact, ville: e.target.value })}
                      placeholder="Limoges"
                      className="input-hh"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-accent w-full flex items-center justify-center gap-2 mt-2"
                >
                  {submitting ? (
                    <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {submitting ? "Envoi en cours..." : "Envoyer ma demande"}
                </button>

                <p className="text-xs text-center text-[#8B7D6B]">
                  Aucun engagement — Réponse sous 24h
                </p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
